import { useState, useEffect } from 'react';
import { Download, X, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (window.navigator.standalone === true) return;
    const dismissed = localStorage.getItem('pwa-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    if (ios) {
      const timer = setTimeout(() => setShow(true), 2000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setShow(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('pwa-dismissed', Date.now().toString());
  };

  if (!show) return null;

  // Android/Chrome — simple one-tap install
  if (!isIOS && deferredPrompt) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          className="fixed bottom-6 right-6 z-[90]"
          data-testid="install-prompt"
        >
          <button
            onClick={handleInstall}
            className="w-14 h-14 bg-amber-500 hover:bg-amber-600 rounded-full shadow-lg shadow-amber-500/30 flex items-center justify-center transition-colors"
            data-testid="install-app-btn"
          >
            <Download className="w-6 h-6 text-white" />
          </button>
          <button onClick={handleDismiss} className="absolute -top-1 -right-1 w-5 h-5 bg-zinc-700 rounded-full flex items-center justify-center">
            <X className="w-3 h-3 text-white" />
          </button>
        </motion.div>
      </AnimatePresence>
    );
  }

  // iOS — small floating button, opens guide on tap
  if (isIOS) {
    return (
      <AnimatePresence>
        {!showGuide ? (
          <motion.div
            key="fab"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="fixed bottom-6 right-6 z-[90]"
            data-testid="install-prompt-ios"
          >
            <button
              onClick={() => setShowGuide(true)}
              className="w-14 h-14 bg-amber-500 hover:bg-amber-600 rounded-full shadow-lg shadow-amber-500/30 flex items-center justify-center transition-colors"
            >
              <Download className="w-6 h-6 text-white" />
            </button>
            <button onClick={handleDismiss} className="absolute -top-1 -right-1 w-5 h-5 bg-zinc-700 rounded-full flex items-center justify-center">
              <X className="w-3 h-3 text-white" />
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="guide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100]"
          >
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowGuide(false)} />
            <motion.div
              initial={{ y: 200 }}
              animate={{ y: 0 }}
              exit={{ y: 200 }}
              className="absolute bottom-0 left-0 right-0 bg-[#1c1c1c] rounded-t-3xl border-t border-white/[0.1] px-6 pt-6 pb-10"
              style={{ paddingBottom: 'max(40px, env(safe-area-inset-bottom))' }}
            >
              <button onClick={() => setShowGuide(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
              <div className="text-center mb-6">
                <h3 className="font-bold text-white text-lg mb-1">Add to Home Screen</h3>
                <p className="text-zinc-400 text-sm">2 quick taps</p>
              </div>
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-4 bg-white/[0.04] rounded-xl p-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">1</div>
                  <p className="text-white text-sm font-medium flex-1">Tap the Share button below</p>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400 flex-shrink-0"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                </div>
                <div className="flex items-center gap-4 bg-white/[0.04] rounded-xl p-4">
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">2</div>
                  <p className="text-white text-sm font-medium flex-1">Tap "Add to Home Screen"</p>
                  <div className="w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0"><span className="text-white text-lg font-light">+</span></div>
                </div>
              </div>
              <motion.div className="flex justify-center" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
                <ArrowDown className="w-6 h-6 text-amber-400" />
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return null;
}
